import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  Building2, Users, Shield, Sparkles, Target, Eye, Heart, Linkedin,
  CheckCircle2, ArrowRight, MapPin, GraduationCap
} from "lucide-react";

const CAMPUS = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1200";
const STUDENTS_LIB = "https://images.pexels.com/photos/6684506/pexels-photo-6684506.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUES = [
  { i: Shield, t: "Verified", d: "Every property KYC-checked. Real photos. Real reviews. Real owners.", c: "emerald" },
  { i: Building2, t: "Scalable", d: "Tooling for owners — leads, payments, marketplace, vendor network.", c: "rose" },
  { i: Users, t: "Community", d: "Students, owners, vendors, colleges — all networked, all transparent.", c: "blue" },
  { i: Sparkles, t: "Transparent", d: "Zero brokerage. Zero hidden fees. Honest pricing across the platform.", c: "amber" },
];

const STORY_BLOCKS = [
  { y: "2024", t: "The problem", d: "A team of ex-students kept hearing the same complaints — broken wifi, surprise rent hikes, mess food gone bad. Finding a PG felt harder than booking a flight." },
  { y: "2024", t: "The idea", d: "Why does PG hunting still rely on brokers, WhatsApp, and luck? We started designing Hostro — not a listings site, but a complete operating system for student housing." },
  { y: "2025", t: "Soft launch", d: "Partnered with 10 PGs in Jaipur. Onboarded Poornima University. First 100 students moved in without a single complaint about hidden fees." },
  { y: "2026", t: "Pan India", d: "Live in 6+ cities. 1,200+ students, 200+ owners, 50+ vendor partners. Building the new standard for student living in India." },
];

export default function About() {
  const [founders, setFounders] = useState([]);
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/founders").then(r => setFounders(r.data)).catch(()=>{});
    api.get("/company").then(r => setC(r.data)).catch(()=>{});
  }, []);

  const founded = c?.founded_year || 2024;
  const valueColor = (k) => ({
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    blue:    "bg-blue-50 text-blue-700 border-blue-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
  })[k] || "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <div className="bg-[#FBFAF6]" data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/70 via-[#FBFAF6] to-rose-50/40 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-semibold uppercase tracking-wider">
              <Sparkles size={14}/> About Hostro
            </div>
            <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl mt-5 text-[#0E1F18]">
              One Platform.<br/>
              <span className="text-emerald-600">Endless</span><br/>
              Possibilities.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] max-w-xl">
              {c?.legal_name || "Hostro Ventures Private Limited"} is building the unified digital backbone for student housing in India — connecting students, PG owners, vendors and colleges in one trustworthy ecosystem. Founded in {founded}.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-xl transition" data-testid="about-cta-register">
                Join Hostro <ArrowRight size={16}/>
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-[#0E1F18] font-semibold px-5 py-3 rounded-xl transition">
                Talk to us
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-3xl overflow-hidden aspect-square border border-gray-200 shadow-lg">
              <img src={CAMPUS} alt="campus" className="w-full h-full object-cover"/>
            </div>
            <div className="rounded-3xl overflow-hidden aspect-square border border-gray-200 shadow-lg mt-10">
              <img src={STUDENTS_LIB} alt="students" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="bg-white border-y border-gray-200 py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl border border-emerald-200 p-8 md:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold uppercase tracking-wider">
              <Target size={14}/> Mission
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-4 leading-tight text-[#0E1F18]">
              {c?.mission || "Simplify and modernize student & co-living accommodation for every Indian student."}
            </h2>
          </div>
          <div className="bg-gradient-to-br from-rose-50 to-white rounded-3xl border border-rose-200 p-8 md:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-semibold uppercase tracking-wider">
              <Eye size={14}/> Vision
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-4 leading-tight text-[#0E1F18]">
              {c?.vision || "India's most trusted student housing and managed-living platform."}
            </h2>
          </div>
        </div>
      </section>

      {/* OUR STORY (timeline) */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 py-20">
        <div className="text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wider">Our Story</span>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 tracking-tight text-[#0E1F18]">From a problem to a platform.</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {STORY_BLOCKS.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl transition relative" data-testid={`story-${i}`}>
              <div className="absolute top-4 right-4 text-3xl font-display font-black text-emerald-100">{s.y}</div>
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{s.t}</div>
              <p className="mt-3 text-[#2a2a2a] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-gradient-to-b from-white to-emerald-50/30 border-y border-gray-200 py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold uppercase tracking-wider">Values</span>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-3 text-[#0E1F18]">What we won&apos;t compromise on.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {VALUES.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:-translate-y-1 transition" data-testid={`value-${i}`}>
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border ${valueColor(v.c)}`}>
                  <v.i size={22}/>
                </div>
                <h3 className="font-display font-black text-xl mt-4 text-[#0E1F18]">{v.t}</h3>
                <p className="mt-2 text-gray-600 text-sm leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      {founders.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-semibold uppercase tracking-wider">Founders</span>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-3 tracking-tight text-[#0E1F18]">The people building it.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {founders.map((f, i) => (
              <div key={i} className="bg-white rounded-3xl border border-gray-200 overflow-hidden hover:shadow-xl transition" data-testid={`founder-${i}`}>
                <div className="aspect-[4/5] overflow-hidden bg-emerald-50">
                  <img
                    src={f.image}
                    alt={f.name}
                    className="w-full h-full object-cover"
                    onError={(e)=>{e.currentTarget.src=`https://ui-avatars.com/api/?name=${encodeURIComponent(f.name)}&size=600&background=25A163&color=FDFCF8&bold=true&font-size=0.4`;}}
                  />
                </div>
                <div className="p-6">
                  <div className="font-display font-black text-2xl text-[#0E1F18]">{f.name}</div>
                  <div className="inline-block px-2.5 py-1 mt-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider border border-emerald-200">{f.role}</div>
                  <p className="text-gray-600 mt-3 text-sm leading-relaxed">{f.bio}</p>
                  {f.linkedin && (
                    <a href={f.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition" data-testid={`founder-linkedin-${i}`}>
                      <Linkedin size={14}/> Connect on LinkedIn
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* STATS — dark band */}
      <section className="bg-[#0E1F18] text-white py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-semibold uppercase tracking-wider">By the numbers</span>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4">Built in India.<br/>For India&apos;s students.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14">
            {[
              { n: "1,200+", l: "Students helped" },
              { n: "200+", l: "Verified owners" },
              { n: "50+", l: "Cities" },
              { n: "4.8/5", l: "Avg rating" },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-display font-black text-5xl md:text-6xl text-emerald-400">{s.n}</div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold uppercase tracking-wider">Who we serve</span>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 text-[#0E1F18]">An ecosystem, not a directory.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {[
            { i: GraduationCap, t: "Students", d: "Search, book, pay rent, order food, raise tickets — all in one app.", c: "emerald" },
            { i: Building2, t: "PG Owners", d: "List, manage, auto-collect rent, track occupancy, grow revenue.", c: "rose" },
            { i: Users, t: "Vendors", d: "Tiffin, laundry, cafés — bring your business online. Get paid faster.", c: "blue" },
            { i: MapPin, t: "Colleges", d: "Welfare panel, student onboarding, scholarship & internship feeds.", c: "amber" },
          ].map((p, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl transition group" data-testid={`audience-${i}`}>
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border ${valueColor(p.c)} group-hover:scale-110 transition`}>
                <p.i size={22}/>
              </div>
              <h3 className="font-display font-black text-xl mt-4 text-[#0E1F18]">{p.t}</h3>
              <p className="mt-2 text-gray-600 text-sm leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 p-10 md:p-16 text-center">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"/>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-rose-300/20 rounded-full blur-3xl"/>
          <div className="relative">
            <Heart size={42} className="mx-auto text-amber-300"/>
            <h2 className="font-display font-black text-4xl md:text-5xl text-white mt-4">Join the movement.</h2>
            <p className="text-lg text-white/90 mt-4 max-w-xl mx-auto">From IIT campuses to tier-2 colleges — Hostro is where housing finally makes sense.</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-white text-[#0E1F18] font-bold px-6 py-3.5 rounded-xl hover:bg-gray-100 transition">
                Sign up free <ArrowRight size={16}/>
              </Link>
              <Link to="/list-property" className="inline-flex items-center justify-center gap-2 bg-[#0E1F18] hover:bg-black text-white font-bold px-6 py-3.5 rounded-xl transition">
                List your PG <Building2 size={16}/>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
